import type {Poster} from '@PosterWhiteboard/poster/poster.class';
import type {Page} from '@PosterWhiteboard/page/page.class';
import type {ItemObject} from '@PosterWhiteboard/items/item/item.types';
import {MOBILE_WHITEBOARD_PADDING} from '@PosterWhiteboard/poster/poster.types';
import {isEditorMobileVariant} from '@Libraries/is-editor-mobile-variant';
import {isPageNavigationBlocked} from '@PosterWhiteboard/libraries/poster-page-navigation-guard.library';
import {isColorBackground} from '@PosterWhiteboard/page/background/background.types';
import {FabricImage, Gradient} from '@postermywall/fabricjs-2';

const PAGE_SWIPE_DISTANCE_THRESHOLD = 0.3;
const PAGE_SWIPE_VELOCITY_THRESHOLD = 0.5;

const ADD_PAGE_DISTANCE_THRESHOLD = 0.65;
const ADD_PAGE_PLACEHOLDER_INITIAL_SCALE = 0.05;

export class PageSwipeManager {
  private readonly poster: Poster;
  private readonly whiteboardContainerElement: HTMLElement;
  private readonly pageContainerParent: HTMLElement | null;

  private isAnimationInProgress = false;
  private isLayoutInitialized = false;
  private swipeStartScrollLeft = 0;
  private swipeStartPageId: string | null = null;
  private swipeStartTime: number | null = null;
  private isAddPageMode = false;
  private addPagePlaceholder: HTMLElement | null = null;
  private fullSwipeDistance = 0;

  constructor(poster: Poster, whiteboardContainerElement: HTMLElement) {
    this.poster = poster;
    this.whiteboardContainerElement = whiteboardContainerElement;
    this.pageContainerParent = whiteboardContainerElement.querySelector<HTMLElement>('.pmw-page-container-parent');
  }

  public canSwipe(): boolean {
    if (this.isAnimationInProgress) {
      return false;
    }

    if (!isEditorMobileVariant() || this.poster.mode.isWebpage() || isPageNavigationBlocked()) {
      return false;
    }

    if (this.poster.isVideo()) {
      return false;
    }

    return this.poster.scaling.isCurrentScaleBestFit(0.03);
  }

  public startSwipe(): void {
    this.poster.clearSelection();
    this.poster.pages.isPageSwiping = true;
    this.swipeStartPageId = this.poster.getCurrentPageId();
    this.isLayoutInitialized = false;
  }

  public onSwipeMove(deltaX: number): void {
    if (deltaX === 0) {
      return;
    }

    if (!this.isLayoutInitialized) {
      this.initializeSwipeMode(deltaX);
    }

    if (this.isAddPageMode) {
      this.updateAddPageAnimation(deltaX);
    } else {
      this.whiteboardContainerElement.scrollLeft -= deltaX;
    }
  }

  public finalizeSwipe(): void {
    if (this.isAddPageMode) {
      this.finalizeAddPageSwipe();
      return;
    }

    const targetPageId = this.resolveTargetPage();
    const targetPage = this.poster.pages.getPage(targetPageId);

    if (targetPage?.pageContainer) {
      const targetScrollLeft = this.computeScrollLeftForElement(targetPage.pageContainer);

      this.poster.clearSelectedItemPageId();
      this.poster.setCurrentPage(targetPageId);

      this.isAnimationInProgress = true;
      this.poster.isRefreshVisibleCanvasDisabled = true;

      this.animateScrollToPosition(targetScrollLeft, () => {
        this.resetState();
      });
    } else {
      this.resetState();
    }
  }

  private initializePageNavLayout(): void {
    const currentPage = this.poster.getCurrentPage();
    if (!currentPage?.pageContainer) {
      return;
    }

    const containerWidth = this.whiteboardContainerElement.clientWidth;
    const pageWidth = currentPage.pageContainer.clientWidth;
    const gap = Math.max(0, (containerWidth - pageWidth) / 2);

    this.whiteboardContainerElement.style.setProperty('--page-swipe-margin', `${gap}px`);
    this.whiteboardContainerElement.style.setProperty('--page-swipe-margin-adjusted', `${gap - MOBILE_WHITEBOARD_PADDING}px`);
    this.whiteboardContainerElement.classList.add('page-swipe-active');

    const prevPage = this.poster.pages.getPreviousPage();
    const nextPage = this.poster.pages.getNextPage();

    prevPage?.showPage();
    nextPage?.showPage();

    // The trailing page (rightmost visible) gets margin-right so the scroll area ends correctly.
    const trailingPage = nextPage ?? currentPage;
    trailingPage?.pageContainer?.classList.add('pmw-swipe-trailing-page');

    // Force a browser reflow so layout is applied before we set scrollLeft.
    void currentPage.pageContainer.offsetLeft;

    const newScrollLeft = this.computeScrollLeftForElement(currentPage.pageContainer);
    this.whiteboardContainerElement.scrollLeft = newScrollLeft;

    this.swipeStartScrollLeft = newScrollLeft;
    this.swipeStartTime = performance.now();

    this.poster.isRefreshVisibleCanvasDisabled = true;
    prevPage?.refreshFullCanvas();
    nextPage?.refreshFullCanvas();

    this.isLayoutInitialized = true;
  }

  private resolveTargetPage(): string {
    const containerWidth = this.whiteboardContainerElement.clientWidth;
    const currentScrollLeft = this.whiteboardContainerElement.scrollLeft;
    const scrollDelta = currentScrollLeft - this.swipeStartScrollLeft;
    const distanceThreshold = containerWidth * PAGE_SWIPE_DISTANCE_THRESHOLD;

    let velocity = 0;
    if (this.swipeStartTime !== null) {
      const timeDelta = performance.now() - this.swipeStartTime;
      if (timeDelta > 0) {
        velocity = Math.abs(scrollDelta) / timeDelta;
      }
    }

    const pageOrder = this.poster.pages.pageOrder;
    const swipeStartPageId = this.swipeStartPageId ?? this.poster.getCurrentPageId();
    let currentPageIndex = pageOrder.indexOf(swipeStartPageId);
    if (currentPageIndex === -1) {
      const currentPageId = this.poster.getCurrentPageId();
      currentPageIndex = pageOrder.indexOf(currentPageId);
      if (currentPageIndex === -1) {
        currentPageIndex = 0;
      }
    }

    let targetPageId = pageOrder[currentPageIndex];

    const meetsDistanceThreshold = Math.abs(scrollDelta) > distanceThreshold;
    const meetsVelocityThreshold = velocity > PAGE_SWIPE_VELOCITY_THRESHOLD;
    const shouldChangePage = meetsDistanceThreshold || meetsVelocityThreshold;

    if (shouldChangePage) {
      if (scrollDelta > 0 && currentPageIndex < pageOrder.length - 1) {
        targetPageId = pageOrder[currentPageIndex + 1];
      } else if (scrollDelta < 0 && currentPageIndex > 0) {
        targetPageId = pageOrder[currentPageIndex - 1];
      }
    }

    return targetPageId;
  }

  private computeScrollLeftForElement(element: HTMLElement): number {
    const containerWidth = this.whiteboardContainerElement.clientWidth;
    const gap = Math.max(0, (containerWidth - element.clientWidth) / 2);
    return element.offsetLeft - (gap - MOBILE_WHITEBOARD_PADDING);
  }

  private animateScrollToPosition(targetX: number, onComplete?: () => void): void {
    const container = this.whiteboardContainerElement;
    const startX = container.scrollLeft;
    const distance = targetX - startX;

    if (Math.abs(distance) < 1) {
      container.scrollLeft = targetX;
      requestAnimationFrame(() => {
        onComplete?.();
      });
      return;
    }

    if (!this.pageContainerParent) {
      container.scrollLeft = targetX;
      requestAnimationFrame(() => {
        onComplete?.();
      });
      return;
    }

    const pageContainerParent = this.pageContainerParent;

    const runAnimation = (): void => {
      pageContainerParent.style.willChange = 'transform';

      const animation = pageContainerParent.animate([{transform: 'translateX(0px)'}, {transform: `translateX(${-distance}px)`}], {
        duration: 160,
        easing: 'linear',
        fill: 'forwards',
      });

      void animation.finished
        .then(() => {
          container.scrollLeft = targetX;
          animation.cancel();

          requestAnimationFrame(() => {
            onComplete?.();
          });
        })
        .catch(() => {
          onComplete?.();
        })
        .finally(() => {
          pageContainerParent.style.willChange = '';
        });
    };

    requestAnimationFrame(runAnimation);
  }

  private initializeSwipeMode(deltaX: number): void {
    const isLastPage = this.poster.pages.getNextPage() === undefined;
    const isSwipingLeft = deltaX < 0;

    if (isLastPage && isSwipingLeft && this.canEnterAddPageMode()) {
      this.initializeAddPageMode();
    } else {
      this.initializePageNavLayout();
    }
  }

  private canEnterAddPageMode(): boolean {
    if (!window.PMW.isMultipageAvailable) {
      return false;
    }
    return this.poster.pages.pageOrder.length < window.PMW.MAX_NUMBER_OF_PAGES;
  }

  private initializeAddPageMode(): void {
    this.isAddPageMode = true;
    this.initializeAddPageLayout();
  }

  private initializeAddPageLayout(): void {
    const currentPage = this.poster.getCurrentPage();
    if (!currentPage?.pageContainer || !currentPage.canvasBackdrop) {
      this.isLayoutInitialized = true;
      return;
    }

    if (!this.pageContainerParent) {
      this.isLayoutInitialized = true;
      return;
    }

    const containerWidth = this.whiteboardContainerElement.clientWidth;
    const pageWidth = currentPage.pageContainer.clientWidth;
    const gap = Math.max(0, (containerWidth - pageWidth) / 2);

    this.whiteboardContainerElement.style.setProperty('--page-swipe-margin-adjusted', `${Math.max(0, gap - MOBILE_WHITEBOARD_PADDING)}px`);
    currentPage.pageContainer.classList.add('pmw-add-page-origin');

    const placeholder = document.createElement('div');
    placeholder.className = 'pmw-add-page-placeholder';
    placeholder.style.width = `${currentPage.canvasBackdrop.clientWidth}px`;
    placeholder.style.height = `${currentPage.canvasBackdrop.clientHeight}px`;
    placeholder.style.marginLeft = `${gap}px`;
    placeholder.style.marginRight = `${Math.max(0, gap - MOBILE_WHITEBOARD_PADDING)}px`;

    placeholder.style.background = this.getPlaceholderBackground(currentPage);
    placeholder.style.backgroundSize = 'cover';
    placeholder.style.backgroundPosition = 'center';

    placeholder.style.transformOrigin = 'left center';
    placeholder.style.transform = `scale(${ADD_PAGE_PLACEHOLDER_INITIAL_SCALE})`;

    this.pageContainerParent.appendChild(placeholder);
    this.addPagePlaceholder = placeholder;

    this.swipeStartScrollLeft = this.whiteboardContainerElement.scrollLeft;

    const placeholderTargetScrollLeft = this.computeScrollLeftForElement(placeholder);
    this.fullSwipeDistance = Math.max(1, placeholderTargetScrollLeft - this.swipeStartScrollLeft);

    // Freeze canvas at full backdrop size — scroll events during drag would otherwise clip it.
    this.poster.isRefreshVisibleCanvasDisabled = true;
    currentPage.refreshFullCanvas();

    this.isLayoutInitialized = true;
  }

  private updateAddPageAnimation(deltaX: number): void {
    if (!this.addPagePlaceholder) {
      return;
    }

    this.whiteboardContainerElement.scrollLeft -= deltaX;

    const totalScrollDelta = this.whiteboardContainerElement.scrollLeft - this.swipeStartScrollLeft;
    const scaleProgress = Math.min(1, Math.max(0, totalScrollDelta / this.fullSwipeDistance));

    const scale = ADD_PAGE_PLACEHOLDER_INITIAL_SCALE + scaleProgress * (1 - ADD_PAGE_PLACEHOLDER_INITIAL_SCALE);

    this.addPagePlaceholder.style.transform = `scale(${scale})`;
  }

  private finalizeAddPageSwipe(): void {
    const totalScrollDelta = this.whiteboardContainerElement.scrollLeft - this.swipeStartScrollLeft;
    const threshold = this.whiteboardContainerElement.clientWidth * ADD_PAGE_DISTANCE_THRESHOLD;

    if (totalScrollDelta >= threshold) {
      this.confirmAddPage();
    } else {
      this.dismissAddPage();
    }
  }

  private confirmAddPage(): void {
    if (!this.addPagePlaceholder) {
      this.resetState();
      return;
    }

    this.isAnimationInProgress = true;
    const placeholder = this.addPagePlaceholder;

    // Start before animation so page is ready before animation completes; bg items copy over via items.
    const sourcePage = this.poster.getCurrentPage();
    const sourceBgItems: Record<string, ItemObject> = {};
    const sourceBgOrder: string[] = [];
    for (const bg of sourcePage.items.getBackgrounds()) {
      const cloned = bg.toObjectWithNewUids() as ItemObject;
      sourceBgItems[cloned.uid] = cloned;
      sourceBgOrder.push(cloned.uid);
    }
    const addPagePromise = this.poster.pages.addPage({
      items: {itemsHashMap: sourceBgItems, itemsOrder: sourceBgOrder},
      zoom: this.poster.scaling.scale,
    });

    const newPageId = this.poster.pages.pageOrder[this.poster.pages.pageOrder.length - 1];
    const newPageForHiding = this.poster.pages.getPage(newPageId);
    if (newPageForHiding?.pageContainer) {
      newPageForHiding.pageContainer.style.visibility = 'hidden';
    }

    requestAnimationFrame(() => {
      placeholder.animate([{transform: placeholder.style.transform}, {transform: 'scale(1)'}], {duration: 160, easing: 'linear', fill: 'forwards'});
    });

    const targetScrollLeft = this.swipeStartScrollLeft + this.fullSwipeDistance;
    this.animateScrollToPosition(targetScrollLeft, () => {
      void addPagePromise
        .then((newPage) => {
          newPage.refreshFullCanvas();
          newPage.fabricCanvas.renderAll();

          newPage.pageContainer.style.visibility = '';
          placeholder.remove();
          this.addPagePlaceholder = null;

          const containerWidth = this.whiteboardContainerElement.clientWidth;
          const oldPageWidth = this.poster.getCurrentPage().pageContainer.clientWidth;
          const gap = Math.max(0, (containerWidth - oldPageWidth) / 2);
          newPage.pageContainer.style.marginLeft = `${gap}px`;
          void newPage.pageContainer.offsetLeft;
          this.poster.setCurrentPage(newPage.hashedID);
          newPage.pageContainer.style.marginLeft = '';
          this.resetState();
        })
        .catch(() => {
          this.resetState();
        });
    });
  }

  private dismissAddPage(): void {
    if (!this.addPagePlaceholder) {
      this.resetState();
      return;
    }

    this.isAnimationInProgress = true;
    const placeholder = this.addPagePlaceholder;

    requestAnimationFrame(() => {
      placeholder.animate([{transform: placeholder.style.transform}, {transform: 'scale(0)'}], {
        duration: 200,
        easing: 'ease-in',
        fill: 'forwards',
      });
    });

    this.animateScrollToPosition(this.swipeStartScrollLeft, () => {
      placeholder.remove();
      this.addPagePlaceholder = null;
      this.resetState();
    });
  }

  // Backgrounds are regular canvas items (never fabricCanvas.backgroundImage /
  // backgroundColor), so the placeholder mirrors the bg active at the current
  // page time: image/video bgs via their fabric image's src, color bgs via
  // their fill.
  private getPlaceholderBackground(page: Page): string {
    const activeBg = page.items.getActiveBackground();
    if (!activeBg) {
      return '#ffffff';
    }

    if (activeBg.fabricObject instanceof FabricImage) {
      const src = activeBg.fabricObject.getSrc();
      if (src) {
        return `url('${src}')`;
      }
    }

    if (isColorBackground(activeBg)) {
      const fill = activeBg.getFillObject();

      if (typeof fill === 'string') {
        return fill;
      }

      if (fill instanceof Gradient) {
        const sortedStops = [...fill.colorStops].sort((a, b) => a.offset - b.offset);

        if (fill.type === 'linear') {
          const stops = sortedStops.map((s) => `${s.color} ${(s.offset - 0.5) * 200}%`).join(', ');
          return `linear-gradient(180deg, ${stops})`;
        }

        const {r2} = fill.coords as {r2: number};
        const scale = (page.canvasBackdrop?.clientWidth ?? page.poster.width) / page.poster.width;
        const radius = r2 * scale;
        const stops = sortedStops.map((s) => `${s.color} ${s.offset * radius}px`).join(', ');
        return `radial-gradient(circle ${radius}px at 50% 50%, ${stops})`;
      }
    }

    return '#ffffff';
  }

  private resetState(): void {
    this.poster.pages.isPageSwiping = false;

    const container = this.whiteboardContainerElement;
    const trailingPage = container.querySelector('.pmw-swipe-trailing-page');
    trailingPage?.classList.remove('pmw-swipe-trailing-page');
    container.classList.remove('page-swipe-active');
    this.poster.updatePagesVisibility();
    if (!this.poster.scaling.isCurrentScaleBestFit(0.001)) {
      this.poster.scaling.fitToScreen(false);
    }
    this.poster.isRefreshVisibleCanvasDisabled = false;
    this.isAnimationInProgress = false;
    this.isLayoutInitialized = false;
    this.swipeStartPageId = null;
    this.swipeStartTime = null;
    this.addPagePlaceholder?.remove();
    this.addPagePlaceholder = null;
    container.querySelector('.pmw-add-page-origin')?.classList.remove('pmw-add-page-origin');
    this.isAddPageMode = false;
    this.fullSwipeDistance = 0;
  }
}
